import { APIError } from '@/plugins/api/error'
import { useV2Api } from '@/plugins/api/openapi'
import store from '@/store'
import { useAlert } from '@/v2/composables/alert/alert'
import { useStorage } from '@vueuse/core'
import dayjs from 'dayjs'
import jwt_decode from 'jwt-decode'
import { isNumber } from 'lodash'
import { defineStore } from 'pinia'
import { computed, nextTick, ref } from 'vue'
import { useRouter } from 'vue-router/composables'
import { useAttendanceShareTokenStore } from './attendance-share-token'
import { useDeviceCodeStore } from './device-code'
import { usePending2FAStore } from './pending-2fa'

export type DecodedToken = {
  user: {
    id: number
    role: string
    name: string
    email: string
    latestAcceptedTermVersion?: number
    hasScreenings: boolean
    hasSeenOnboarding: boolean
    aditionalRoutes: string[]
  }
  session: {
    id: string
    duration: number // ms
  }
  exp: Date
  iat: Date
}

export enum UserType {
  PERSON = 'PERSON',
  CLINIC = 'CLINIC',
  ADMIN = 'ADMIN',
}

// export type PharmaceuticalDomainConfig = {
//   NAME: string
//   TITLE: string
//   PROJECT_TITLE: string
//   LOGO_LOGIN: string
//   LOGO_TEXTO_MENU: string
//   LOGO_MENU: string
//   SHOW_LOGO_MENU_AND_LOGO_TEXT: boolean
//   TERM_COMPONENT: unknown
//   FAVICON: string
//   PHARMACEUTICAL_ID: number
//   SUMMARIZEDFLOW: boolean
//   CUSTOMSUMMARIZEDFLOW: boolean | string
//   SUMMARIZEDFLOWKEYDEFAULT: boolean
//   DISPLAYSUMMARIZEDFLOWKEY: boolean
//   CLINIC_APPROVAL: boolean
// }

export const useAuthStore = defineStore('auth', () => {
  const api = useV2Api()
  const router = useRouter()
  const alert = useAlert()
  const deviceCodeStore = useDeviceCodeStore()
  const pending2FAStore = usePending2FAStore()
  const attendanceShareTokenStore = useAttendanceShareTokenStore()

  // const pharmaceuticalDomainConfigMap = PHARMACEUTICAL_DOMAIN_CONFIG as Record<
  //   string,
  //   Record<number | 'default', PharmaceuticalDomainConfig>
  // >

  const _token = useStorage<string | null>('kintsu-token', null, localStorage)
  function setToken(rawToken: string | null) {
    _token.value = rawToken
    if (rawToken) {
      incrementVisitCount()
    }
  }

  async function getTokenfromApi({
    login,
    password,
    codeToLogin,
  }: {
    login: string
    password?: string
    codeToLogin?: string
  }) {
    if (!password && !codeToLogin) {
      throw new APIError({
        message: APIError.messageFallback,
        description: 'Verifique se as chaves de acesso estão corretas',
      })
    }

    const loginByCode = !!codeToLogin
    if (loginByCode) {
      return await api.auth.loginByVerificationCode({
        email: login,
        code: codeToLogin,
        acceptLatestTerm: true,
      })
    }

    return await api.auth.login({ email: login, password: password! })
  }

  async function login({ login, password, codeToLogin }: { login: string; password?: string; codeToLogin?: string }) {
    try {
      const { token, type, deviceCode } = await getTokenfromApi({ login, password, codeToLogin })

      if (type === 'pending') {
        pending2FAStore.token = token
        deviceCodeStore.removeFromMap(login)
        await nextTick()
        await router.push({ name: 'login-2fa' })
        return true
      }

      if (deviceCode) {
        deviceCodeStore.addIntoMap(login, deviceCode)
      }

      _token.value = token
      await store.dispatch('auth/setToken', _token.value)
      await nextTick()
      setTimeout(() => {
        pending2FAStore.token = null
        router.push({ name: 'home' })
      }, 100)
      return true
    } catch (error) {
      alert.error({ error })
      return false
    }
  }

  async function logout() {
    try {
      await api.auth.logout()
      await store.dispatch('auth/clearCredentials')

      setToken(null)
      setDigitalSignatureToken(null, null, false)
      await nextTick()
      router.push({ name: 'login' })
    } catch (error) {
      alert.error({ error })
    }
  }

  const _showSurvey = ref(true)

  const _initiated = useStorage<boolean>('kintsu-initiated', false, sessionStorage)
  const initiated = computed(() => _initiated.value)
  const setInitiated = (value: boolean) => {
    _initiated.value = value
  }

  const session = computed(() => {
    if (!_token.value) {
      return null
    }
    try {
      return jwt_decode<DecodedToken>(_token.value)
    } catch (error) {
      console.error(error)
      return null
    }
  })

  const userId = computed(() => session.value?.user.id)
  const userType = computed(() => {
    if (!session.value) {
      return undefined
    }
    if (session.value.user.role === 'Admin') {
      return UserType.ADMIN
    }

    if (session.value.user.role === 'Physician') {
      return UserType.PERSON
    }
    return undefined
  })
  const userEmail = computed(() => session.value?.user.email)
  const isAdmin = computed(() => session.value?.user.role === 'Admin')
  const userRole = computed(() => session.value?.user.role)
  const userHasScreenings = computed(() => session.value?.user.hasScreenings)
  const aditionalRoutes = computed(() => session.value?.user.aditionalRoutes)
  const isPhysician = computed(() => session.value?.user.role === 'Physician')
  const isLogged = computed(
    () =>
      !attendanceShareTokenStore.token &&
      !!session.value &&
      !!session.value.user.id &&
      dayjs().isAfter(session.value.exp),
  )

  const digitalSignatureToken = useStorage<{ token: string | null; expiresAt: Date | null; authenticated: boolean }>(
    'digital-signature-token',
    { token: null, expiresAt: null, authenticated: false },
    localStorage,
  )

  function setDigitalSignatureToken(rawToken: string | null, expiresAt: Date | null, authenticated: boolean) {
    digitalSignatureToken.value.token = rawToken
    digitalSignatureToken.value.expiresAt = expiresAt
    digitalSignatureToken.value.authenticated = authenticated
  }

  function setDigitalSignatureAuthentication(authenticated: boolean) {
    digitalSignatureToken.value.authenticated = authenticated
  }

  const hasSession = computed(() => !!session.value && !!session.value.session.id)

  const hasUserBeenDefined = computed(() => session.value?.user.id)

  const visitCount = useStorage('visit-count', 0, localStorage)
  function incrementVisitCount() {
    if (isNumber(visitCount.value)) {
      visitCount.value += 1
    }
  }

  return {
    _token,
    login,
    logout,
    _showSurvey,
    // _defaultHomePage,
    // _currentHomePage,
    _initiated,
    session,
    initiated,
    setInitiated,
    // setCurrentHomePage,
    // pharmaceuticalDomainConfig,
    userId,
    userEmail,
    isLogged,
    hasSession,
    isPhysician,
    isAdmin,
    userType,
    userRole,
    aditionalRoutes,
    userHasScreenings,
    setToken,
    // currentPharmaceutical,
    visitCount,
    hasUserBeenDefined,
    digitalSignatureToken,
    setDigitalSignatureToken,
    setDigitalSignatureAuthentication,
  }
})
